import React, { useEffect, useState, useMemo } from "react";
import { Button, Card, Col, Row, Modal, Spinner } from "react-bootstrap";
import { Link } from "react-router-dom";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import PageLoader from "../components/PageLoader";
import { documentsApi } from "../api/documents";

export default function DocumentsList() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("table");
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(8);
  const [skin, setSkin] = useState(localStorage.getItem("skin-mode") ? "dark" : "");

  // État du modal de suppression
  const [deleteTarget, setDeleteTarget] = useState(null); // { id, title } | null
  const [deleting, setDeleting] = useState(false);

  // Chargement
  useEffect(() => {
    setLoading(true);
    documentsApi
      .list()
      .then((data) => setDocuments(data || []))
      .catch((err) => console.error("Erreur chargement documents:", err))
      .finally(() => setLoading(false));
  }, []);

  // Ouvre le modal
  const askDelete = (doc) => {
    setDeleteTarget({ id: doc.id, title: doc.title });
  };

  // Confirme la suppression
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await documentsApi.destroy(deleteTarget.id);
      setDocuments((prev) => prev.filter((d) => d.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err) {
      alert("Erreur lors de la suppression : " + (err.response?.data?.error || err.message));
    } finally {
      setDeleting(false);
    }
  };

  // Filtrage + pagination
  const filteredDocs = useMemo(() => {
    if (!search) return documents;
    const q = search.toLowerCase();
    return documents.filter(
      (d) =>
        d.title?.toLowerCase().includes(q) ||
        d.slug?.toLowerCase().includes(q) ||
        d.description?.toLowerCase().includes(q)
    );
  }, [documents, search]);

  const totalPages = Math.max(1, Math.ceil(filteredDocs.length / perPage));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedDocs = useMemo(() => {
    const start = (safePage - 1) * perPage;
    return filteredDocs.slice(start, start + perPage);
  }, [filteredDocs, safePage, perPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, perPage]);

  const pageNumbers = useMemo(() => {
    const pages = [];
    const maxVisible = 5;
    if (totalPages <= maxVisible + 2) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (safePage > 3) pages.push("…");
      const start = Math.max(2, safePage - 1);
      const end = Math.min(totalPages - 1, safePage + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      if (safePage < totalPages - 2) pages.push("…");
      pages.push(totalPages);
    }
    return pages;
  }, [totalPages, safePage]);

  if (loading) {
    return (
      <React.Fragment>
        <Header onSkin={setSkin} />
        <div className="main main-app cere-main p-3 p-lg-4">
          <PageLoader message="Chargement des documents…" />
        </div>
        <Footer />
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app cere-main p-3 p-lg-4">

        {/* En-tête de page */}
        <div className="cere-page-head d-md-flex align-items-center justify-content-between mb-4">
          <div>
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb cere-breadcrumb fs-sm mb-1">
                <li className="breadcrumb-item"><Link to="/dashboard">Admin</Link></li>
                <li className="breadcrumb-item active">Documents</li>
              </ol>
            </nav>
            <h4 className="cere-page-title mb-0">Bibliothèque de documents</h4>
            <p className="cere-page-subtitle mb-0">
              Gérez les documents de la bibliothèque numérique du CERE
            </p>
          </div>
          <div className="d-flex gap-2 mt-3 mt-md-0">
            <Button as={Link} to="/documents/new" className="cere-btn cere-btn-primary">
              <i className="ri-add-line me-2"></i>
              Nouveau document
            </Button>
          </div>
        </div>

        {/* Toolbar */}
        <Card className="cere-card cere-toolbar-card mb-4">
          <Card.Body>
            <Row className="g-3 align-items-center">
              <Col md="5">
                <div className="cere-search-input">
                  <i className="ri-search-line"></i>
                  <input
                    type="text"
                    placeholder="Rechercher un document…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  {search && (
                    <button
                      type="button"
                      className="cere-search-clear"
                      onClick={() => setSearch("")}
                      aria-label="Effacer la recherche"
                    >
                      <i className="ri-close-line"></i>
                    </button>
                  )}
                </div>
              </Col>

              <Col md="4" className="d-flex align-items-center gap-2">
                <span className="cere-toolbar-label">Afficher</span>
                <select
                  className="cere-select"
                  value={perPage}
                  onChange={(e) => setPerPage(Number(e.target.value))}
                >
                  <option value={4}>4</option>
                  <option value={8}>8</option>
                  <option value={12}>12</option>
                  <option value={20}>20</option>
                </select>
                <span className="cere-toolbar-label">par page</span>
              </Col>

              <Col md="3" className="d-flex justify-content-md-end align-items-center gap-2">
                <span className="cere-toolbar-label me-2">
                  {filteredDocs.length} doc{filteredDocs.length > 1 ? "s" : ""}
                </span>
                <div className="cere-view-toggle">
                  <button
                    className={`cere-view-btn ${view === "table" ? "active" : ""}`}
                    onClick={() => setView("table")}
                    title="Vue tableau"
                  >
                    <i className="ri-table-line"></i>
                  </button>
                  <button
                    className={`cere-view-btn ${view === "cards" ? "active" : ""}`}
                    onClick={() => setView("cards")}
                    title="Vue cartes"
                  >
                    <i className="ri-grid-line"></i>
                  </button>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Contenu */}
        {filteredDocs.length === 0 ? (
          <Card className="cere-card cere-empty-state">
            <Card.Body>
              <div className="cere-empty-icon">
                <i className="ri-file-search-line"></i>
              </div>
              <h5 className="cere-empty-title">Aucun document trouvé</h5>
              <p className="cere-empty-desc">
                {search
                  ? `Aucun résultat pour « ${search} ». Essayez un autre mot-clé.`
                  : "La bibliothèque est vide. Commencez par ajouter un document."}
              </p>
              <Button as={Link} to="/documents/new" className="cere-btn cere-btn-primary">
                <i className="ri-add-line me-2"></i>
                Ajouter un document
              </Button>
            </Card.Body>
          </Card>
        ) : view === "table" ? (
          <Card className="cere-card cere-table-card">
            <div className="table-responsive">
              <table className="cere-table-docs">
                <thead>
                  <tr>
                    <th style={{ width: 56 }}>#</th>
                    <th>Titre</th>
                    <th>Catégorie</th>
                    <th>Source</th>
                    <th style={{ width: 80 }}>Année</th>
                    <th style={{ width: 80 }}>Format</th>
                    <th style={{ width: 90 }}>Pop.</th>
                    <th style={{ width: 120 }} className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedDocs.map((doc) => (
                    <tr key={doc.id}>
                      <td><span className="cere-doc-id">#{doc.id}</span></td>
                      <td>
                        <div className="cere-doc-info">
                          <div className="cere-doc-info-icon">
                            <i className="ri-file-text-line"></i>
                          </div>
                          <div className="cere-doc-info-text">
                            <Link
                              to={`/documents/${doc.id}/edit`}
                              className="cere-doc-info-title has-tooltip"
                              data-tooltip={doc.title}
                            >
                              {doc.title}
                            </Link>
                            <div className="cere-doc-info-slug has-tooltip" data-tooltip={doc.slug}>
                              {doc.slug}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="cere-chip cere-chip--blue">
                          {doc.category?.name || "—"}
                        </span>
                      </td>
                      <td>
                        <span className="cere-doc-source">{doc.source?.name || "—"}</span>
                      </td>
                      <td>{doc.publicationYear || "—"}</td>
                      <td>
                        <span className="cere-chip cere-chip--green">
                          {(doc.fileType || "pdf").toUpperCase()}
                        </span>
                      </td>
                      <td>
                        <span className="cere-doc-pop">
                          <i className="ri-download-line"></i>
                          {doc.downloadCount || 0}
                        </span>
                      </td>
                      <td className="text-end">
                        <div className="cere-doc-actions">
                          <Link
                            to={`/documents/${doc.id}/edit`}
                            className="cere-icon-btn"
                            title="Éditer"
                          >
                            <i className="ri-edit-line"></i>
                          </Link>
                          <button
                            className="cere-icon-btn cere-icon-btn--danger"
                            onClick={() => askDelete(doc)}
                            title="Supprimer"
                          >
                            <i className="ri-delete-bin-line"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        ) : (
          <Row className="g-3">
            {paginatedDocs.map((doc) => (
              <Col sm="6" lg="4" xl="3" key={doc.id}>
                <Card className="cere-card cere-doc-card h-100">
                  <Card.Body>
                    <div className="cere-doc-card-top">
                      <span className="cere-chip cere-chip--green">
                        {(doc.fileType || "PDF").toUpperCase()}
                      </span>
                      <span className="cere-doc-id">#{doc.id}</span>
                    </div>

                    <h6 className="cere-doc-card-title">{doc.title}</h6>
                    <p className="cere-doc-card-desc">
                      {doc.description ? doc.description.slice(0, 90) + "…" : "Sans description"}
                    </p>

                    <div className="cere-doc-card-meta">
                      <span title={doc.category?.name}>
                        <i className="ri-price-tag-3-line"></i>
                        <span className="cere-doc-card-meta-text">{doc.category?.name || "—"}</span>
                      </span>
                      <span title={doc.source?.name}>
                        <i className="ri-building-bank-line"></i>
                        <span className="cere-doc-card-meta-text">{doc.source?.name || "—"}</span>
                      </span>
                    </div>

                    <div className="cere-doc-card-footer">
                      <span className="cere-doc-pop">
                        <i className="ri-download-line"></i>
                        {doc.downloadCount || 0}
                      </span>
                      <div className="cere-doc-actions">
                        <Link
                          to={`/documents/${doc.id}/edit`}
                          className="cere-icon-btn"
                          title="Éditer"
                        >
                          <i className="ri-edit-line"></i>
                        </Link>
                        <button
                          className="cere-icon-btn cere-icon-btn--danger"
                          onClick={() => askDelete(doc)}
                          title="Supprimer"
                        >
                          <i className="ri-delete-bin-line"></i>
                        </button>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="cere-pagination-wrapper">
            <div className="cere-pagination">
              <button
                className="cere-page-btn"
                disabled={safePage === 1}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                aria-label="Page précédente"
              >
                <i className="ri-arrow-left-s-line"></i>
              </button>

              {pageNumbers.map((p, i) =>
                p === "…" ? (
                  <span key={`ellipsis-${i}`} className="cere-page-ellipsis">…</span>
                ) : (
                  <button
                    key={p}
                    className={`cere-page-btn ${p === safePage ? "active" : ""}`}
                    onClick={() => setCurrentPage(p)}
                  >
                    {p}
                  </button>
                )
              )}

              <button
                className="cere-page-btn"
                disabled={safePage === totalPages}
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                aria-label="Page suivante"
              >
                <i className="ri-arrow-right-s-line"></i>
              </button>
            </div>
            <div className="cere-pagination-info">
              Page {safePage} sur {totalPages}
            </div>
          </div>
        )}

        {/* Modal de confirmation de suppression */}
    <Modal
  show={!!deleteTarget}
  onHide={() => !deleting && setDeleteTarget(null)}
  centered
  backdrop="static"
  className="cere-modal-delete"
>
  <div className="cere-modal-delete-icon">
    <i className="ri-delete-bin-line"></i>
  </div>

  <Modal.Body className="text-center">
    <h4 className="cere-modal-delete-title">Confirmer la suppression</h4>
    <p className="cere-modal-delete-text">
      Êtes-vous sûr de vouloir supprimer ce document ? Cette action est définitive.
    </p>

    {deleteTarget && (
  <div className="cere-modal-delete-preview">
    <i className="ri-file-text-line"></i>
    <div className="cere-modal-delete-preview-content">
      <span className="cere-modal-delete-preview-label">Document ciblé</span>
      <span 
        className="cere-modal-delete-preview-name"
        title={deleteTarget.title}
      >
        {deleteTarget.title}
      </span>
    </div>
  </div>
)}
    <div className="cere-modal-delete-warning-box">
      <i className="ri-error-warning-line"></i>
      <span>Cette action est irréversible.</span>
    </div>
  </Modal.Body>

  <Modal.Footer className="cere-modal-delete-footer">
    <button
      className="cere-btn cere-btn-outline"
      onClick={() => setDeleteTarget(null)}
      disabled={deleting}
    >
      Annuler
    </button>
    <button
      className="cere-btn cere-btn-danger"
      onClick={confirmDelete}
      disabled={deleting}
    >
      {deleting ? (
        <>
          <Spinner animation="border" size="sm" className="me-2" />
          Suppression…
        </>
      ) : (
        <>
          <i className="ri-delete-bin-line me-2"></i>
          Supprimer
        </>
      )}
    </button>
  </Modal.Footer>
</Modal>

        <Footer />
      </div>
    </React.Fragment>
  );
}